import { PlusOutlined } from '@ant-design/icons';
import { AppState } from '@auth0/auth0-react';
import AppAttachment from '@nxsys/components/AppAttachment';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import AppIconButton from '@nxsys/components/AppIconButton';
import { IconDownload } from '@nxsys/components/AppIconSvg';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsDeleteIcon from '@nxsys/components/AppsDeleteIcon';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { defaultDocumentTypeOptions } from '@nxsys/constants/AppConstants';
import {
  useCreateDocumentEmployee,
  useDeleteDocumentEmployee,
  useUpdateDocumentEmployee,
} from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch } from '@nxsys/store';
import { appActions } from '@nxsys/store/slices/app/reducer/appSlice';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetDocumentEmployeeListParams } from '@nxsys/store/slices/employee/types';
import {
  CreateDocumentEmployeeParams,
  UpdateDocumentEmployeeParams,
} from '@nxsys/types/api';
import { Icons } from '@nxsys/types/icons';
import { DocumentPayload, DocumentType } from '@nxsys/types/models/document';
import { formatStatus } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { showToast } from '@nxsys/utils/toast';
import { Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DetailOnboardingModal from '../../documents/Listing/DocumentList/DocumentEmployee/DetailOnboardingModal';
import DetailOtherModal from '../../documents/Listing/DocumentList/DocumentEmployee/DetailOtherModal';
import CreateDocumentModal from './components/CreateDocumentModal';
import UpdateDocumentModal from './components/UpdateDocumentModal';
import { debounce } from 'lodash';


enum DOCUMENT_TAB {
  ONBOARDING = 'ONBOARDING',
  OTHER = 'OTHER',
}

type DocumentProps = {
  employeeId: string;
  defaultTab?: 'ONBOARDING' | 'OTHER';
};

const Document: React.FC<DocumentProps> = ({ employeeId, defaultTab }) => {
  const { messages, formatMessage } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();

  const tabs = useMemo(
    () => [
      { tab: messages['common.onboarding'], key: DOCUMENT_TAB.ONBOARDING },
      {
        tab: messages['common.other'],
        key: DOCUMENT_TAB.OTHER,
      },
    ],
    [],
  );

  const [activeTab, setActiveTab] = useState<DOCUMENT_TAB>(
    defaultTab ? DOCUMENT_TAB[defaultTab] : DOCUMENT_TAB.ONBOARDING,
  );

  useEffect(() => {
    if (defaultTab) {
      setActiveTab(DOCUMENT_TAB[defaultTab]);
    }
  }, [defaultTab]);

  const handleTabChange = (key: DOCUMENT_TAB) => {
    setActiveTab(key);
  };

  const { documentList } = useSelector(
    (state: AppState) => state.employeeStore,
  );
  const { list, pagination, loading, queryParams } = documentList;

  const handleGetDocuments = (params: GetDocumentEmployeeListParams) => {
    const newParams = {
      ...params,
      id: employeeId,
      ...(activeTab === DOCUMENT_TAB.ONBOARDING && { isOnboarding: true }),
    };
    dispatch(employeeActions.getDocumentEmployeeList(newParams));
  };

  const refetchData = () => {
    handleGetDocuments({ ...queryParams });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleGetDocuments({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleGetDocuments({
        ...queryParams,
        sortField: sorter.field || '',
        sortType: sorter.order,
      });
    } else {
      handleGetDocuments({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  useEffect(() => {
    handleGetDocuments({ page: 0, size: 20 });
  }, [activeTab]);

  const { run: deleteDocumentEmployee } = useDeleteDocumentEmployee();
  const onDeleteDocument = async (id: string) => {
    const res = await deleteDocumentEmployee(id, employeeId);
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.deletedSuccess'],
      });
      refetchData();
    }
  };

  const columns: {
    [DOCUMENT_TAB.OTHER]: ColumnsType<DocumentType>;
    [DOCUMENT_TAB.ONBOARDING]: ColumnsType<DocumentType>;
  } = {
    [DOCUMENT_TAB.OTHER]: [
      {
        key: 'actions',
        title: 'Actions',
        fixed: 'left',
        align: 'left',
        width: 200,
        render: (record) => (
          <ul className="table-actions flex items-center justify-start">
            <li>
              <AppIconButton
                title="View"
                icon={<Icons.IconEye />}
                onClick={() => onToggleDetailDocument(record)}
              />
            </li>
            <li>
              <AppIconButton
                title="Edit"
                icon={<Icons.IconEdit />}
                onClick={() => onToggleUpdateDocument(record)}
              />
            </li>
            <li>
              <AppIconButton
                title="Download"
                icon={<IconDownload disabled={!record?.documentFile} />}
                disabled={!record?.documentFile}
                onClick={() => window.open(record?.documentFile, '_blank')}
              />
            </li>
            <li>
              <AppsDeleteIcon
                deleteAction={() => onDeleteDocument(record?.id)}
                title="Delete Document"
                paragraph={
                  <p>
                    {formatMessage(
                      {
                        id: 'common.deleteDocumentConfirmWithId',
                      },
                      { id: record.id },
                    )}
                  </p>
                }
              />
            </li>
          </ul>
        ),
      },
      {
        key: 'id',
        title: 'Doc ID',
        fixed: 'left',
        dataIndex: 'id',
        sorter: true,
        sortDirections: ['descend', 'ascend'],
      },
      {
        key: 'agencyRef',
        title: messages['common.agencyRef'],
        dataIndex: 'subcontractorRef',
        render: (value) =>
          `${value?.primaryAgency?.agencyPrefix || '___'}/${
            value?.agencyRef || value?.systemId || '_'
          }`,
      },
      {
        key: 'subcontractorRef',
        title: messages['common.subcontractorName'],
        dataIndex: 'subcontractorRef',
        width: 200,
        render: (author) => {
          return (
            <span>{`${author?.forename || ''} ${author?.surname || ''}`}</span>
          );
        },
      },
      {
        key: 'documentTitle',
        title: messages['common.documentTitle'],
        dataIndex: 'documentTitle',
        width: 300,
        render: (value) => value || '_',
      },
      {
        key: 'documentType',
        title: messages['common.documentType'],
        dataIndex: 'documentType',
        width: 250,
        render: (value) =>
          defaultDocumentTypeOptions.find((item) => item?.value === value)
            ?.label || '_',
      },
      {
        key: 'documentFile',
        title: messages['common.attachment'],
        width: 300,
        render: ({ documentFile, documentTitle }) =>
          documentFile ? (
            <AppAttachment urlFile={documentFile} title={documentTitle} />
          ) : (
            '_'
          ),
      },
      {
        key: 'expiryDate',
        title: messages['common.expiryDate'],
        dataIndex: 'expiryDate',
        sorter: true,
        sortDirections: ['descend', 'ascend'],
        render: (value) => (
          <span>
            {value
              ? moment(value).tz('Europe/London').format('DD/MM/YYYY')
              : '_'}
          </span>
        ),
      },
      {
        key: 'createdAt',
        title: messages['common.receivedOn'],
        dataIndex: 'createdAt',
        sorter: true,
        sortDirections: ['descend', 'ascend'],
        render: (value) => (
          <span>
            {value
              ? moment(value).tz('Europe/London').format('DD/MM/YYYY')
              : '_'}
          </span>
        ),
      },
      {
        key: 'documentDate',
        title: messages['common.documentDate'],
        dataIndex: 'documentDate',
        width: 200,
        sorter: true,
        sortDirections: ['descend', 'ascend'],
        render: (value) => (
          <span>
            {value
              ? moment(value).tz('Europe/London').format('DD/MM/YYYY')
              : '_'}
          </span>
        ),
      },
    ],
    [DOCUMENT_TAB.ONBOARDING]: [
      {
        key: 'actions',
        title: 'Actions',
        fixed: 'left',
        align: 'left',
        render: (record) => (
          <ul className="flex items-center justify-start gap-x-2">
            <li>
              <AppIconButton
                title="View"
                icon={<Icons.IconEye />}
                onClick={() => onToggleDetailOnboardingDocument(record)}
              />
            </li>
            <li>
              <AppIconButton
                title="Download"
                icon={<IconDownload disabled={!record?.documentFile} />}
                disabled={!record?.documentFile}
                onClick={() => window.open(record?.documentFile, '_blank')}
              />
            </li>
          </ul>
        ),
      },
      {
        key: 'id',
        title: 'Doc ID',

        fixed: 'left',
        dataIndex: 'id',
        sorter: true,
        sortDirections: ['descend', 'ascend'],
      },
      {
        key: 'documentStatus',
        title: messages['common.status'],

        dataIndex: 'documentStatus',
        render: (value) => (
          <AppTagStatus className={formatStatus(value || '')}>
            {formatStatus(value || '_')}
          </AppTagStatus>
        ),
      },
      {
        key: 'documentShareCode',
        title: messages['table.shareCodeVerified'],
        dataIndex: 'documentShareCode',

        width: 300,
        render: (value) => value || '_',
      },
      {
        key: 'documentTitle',
        title: messages['common.documentTitle'],
        dataIndex: 'documentTitle',

        width: 300,
        render: (value) => value || '_',
      },
      {
        key: 'documentFile',
        title: messages['common.attachment'],
        width: 400,

        render: ({ documentFile, documentTitle }) =>
          documentFile ? (
            <AppAttachment urlFile={documentFile} title={documentTitle} />
          ) : (
            '_'
          ),
      },
      {
        key: 'subcontractorRef',
        title: messages['common.subcontractorName'],
        width: 200,

        render: (record) => {
          const author =
            record?.subcontractorRef || record?.prospectsOnboardingRef;
          return (
            <span>{`${author?.forename || ''} ${author?.surname || ''}`}</span>
          );
        },
      },
      {
        key: 'agencyRef',
        title: messages['common.agencyName'],

        render: (record) => {
          const author =
            record?.prospectsOnboardingRef?.agency || record?.agencyRef;
          return <span>{author?.name || '_'}</span>;
        },
      },
      {
        key: 'createdBy',
        title: messages['common.createdBy'],

        render: (record) => {
          const author =
            record?.subcontractorRef || record?.prospectsOnboardingRef;
          return (
            <span>{`${author?.forename || ''} ${author?.surname || ''}`}</span>
          );
        },
      },
      {
        key: 'completedDate',
        title: messages['common.completedOn'],
        dataIndex: 'subcontractorRef',

        render: (value) => (
          <span>
            {value?.createdAt
              ? moment(value.createdAt).tz('Europe/London').format('DD/MM/YYYY')
              : '_'}
          </span>
        ),
      },
      {
        key: 'userRef',
        title: messages['common.completedBy'],
        dataIndex: 'prospectsOnboardingRef',

        render: (record) => {
          const author = record?.userRef;
          return (
            <span>{`${author?.forename || ''} ${author?.surname || ''}`}</span>
          );
        },
      },
    ],
  };

  const [isOpenDetail, setIsOpenDetail] = useState(false);
  const [isOpenDetailOnboarding, setIsOpenDetailOnboarding] = useState(false);
  const [documentSelected, setDocumentSelected] = useState<DocumentType | null>(
    null,
  );

  const onToggleDetailDocument = (document?: DocumentType) => {
    setIsOpenDetail(!isOpenDetail);
    setDocumentSelected(document || null);
  };

  const onToggleDetailOnboardingDocument = (document?: DocumentType) => {
    setIsOpenDetailOnboarding(!isOpenDetailOnboarding);
    setDocumentSelected(document || null);
  };

  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const onToggleCreateDocument = () => {
    setIsOpenCreate(!isOpenCreate);
  };

  const { run: createDocumentEmployee } = useCreateDocumentEmployee();
  const onCreatedDocument = async (values: DocumentPayload) => {
    const params: CreateDocumentEmployeeParams = {
      documentType: values?.documentType || '',
      documentTitle: values?.documentTitle || '',
      documentDate: values?.documentDate || null,
      expiryDate: values?.expiryDate || null,
      documentFile: null,
    };

    if (values?.documentFile) {
      const { url } = await dispatch(
        appActions.uploadFile({ file: values?.documentFile as File }),
      ).unwrap();
      // NX-6056: the thunk returns the S3 object key; store it as-is.
      params.documentFile = url;
    }
    const res = await createDocumentEmployee(employeeId, params);
    if (res.success) {
      showToast({
        type: 'success',
        message: messages['documents.add.success'],
      });
      refetchData();
    }
  };

  const [isOpenUpdate, setIsOpenUpdate] = useState(false);
  const onToggleUpdateDocument = (document?: DocumentType) => {
    setIsOpenUpdate(!isOpenUpdate);
    setDocumentSelected(document || null);
  };

  const { run: updateDocumentEmployee } = useUpdateDocumentEmployee();
  const onUpdateDocument = async (
    documentId: string,
    values: DocumentPayload,
  ) => {
    const params: UpdateDocumentEmployeeParams = {
      documentType: values?.documentType || '',
      documentTitle: values?.documentTitle || '',
      documentDate: values?.documentDate || null,
      expiryDate: values?.expiryDate || null,
      documentFile: (values?.documentFile as string) || null,
    };
    const isUploadNewFile = Boolean(
      (values?.documentFile as { name: string; type: string; size: number })
        .name,
    );
    if (isUploadNewFile) {
      const { url } = await dispatch(
        appActions.uploadFile({ file: values?.documentFile as File }),
      ).unwrap();
      // NX-6056: the thunk returns the S3 object key; store it as-is.
      params.documentFile = url;
    }
    const res = await updateDocumentEmployee(documentId, employeeId, params);
    if (res.success) {
      showToast({
        type: 'success',
        message: messages['documents.update.success'],
      });
      refetchData();
    }
  };

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleGetDocuments({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedFetchData(value);
  };

  const tabLabel =
    activeTab === DOCUMENT_TAB.ONBOARDING
      ? messages['common.onboarding']
      : messages['common.other'];

  const tableContent = (
    <>
      <div className="flex justify-between items-center mb-[16px]">
        <AppInputSearch onChange={onChangeSearch} />
        {activeTab === DOCUMENT_TAB.OTHER && (
          <AppButton
            type="primary"
            name={messages['common.addNew']}
            leftIcon={<PlusOutlined />}
            onClick={onToggleCreateDocument}
          />
        )}
      </div>
      <AppsContent>
        <AppTableContainer
          data={list || []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 2500, y: 700 }}
          titleEmpty={messages[`documents.empty.${activeTab}`] as string}
          columns={columns[activeTab]}
          onChangeTable={onChangeTable}
          onChangePagination={onChangePagination}
        />
      </AppsContent>
    </>
  );

  const modals = (
    <>
      <DetailOtherModal
        title={messages['common.documentDetail']}
        isOpen={isOpenDetail}
        documentSelected={documentSelected}
        toggle={onToggleDetailDocument}
        onDeleteDocument={onDeleteDocument}
        toggleUpdate={() => {}}
      />
      <DetailOnboardingModal
        title={messages['common.documentDetail']}
        isOpen={isOpenDetailOnboarding}
        documentSelected={documentSelected}
        toggle={onToggleDetailOnboardingDocument}
        hideVerify
        onVerifyDocument={() => {}}
      />
      <CreateDocumentModal
        title={messages['common.addNew']}
        isOpen={isOpenCreate}
        toggle={onToggleCreateDocument}
        onCreatedDocument={onCreatedDocument}
      />
      <UpdateDocumentModal
        title={messages['common.editDocument']}
        isOpen={isOpenUpdate}
        documentSelected={documentSelected}
        toggle={onToggleUpdateDocument}
        onUpdateDocument={onUpdateDocument}
      />
    </>
  );

  // V2 flat layout when defaultTab is provided (sidebar navigation)
  if (defaultTab) {
    return (
      <div>
        <Typography.Title
          level={5}
          className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
        >
          {`${messages['common.document']} - ${tabLabel}`}
        </Typography.Title>
        {tableContent}
        {modals}
      </div>
    );
  }

  // Legacy card layout
  return (
    <AppCard title={messages['common.document']}>
      <AppCardTabListContainer
        tabList={tabs}
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        className="!border-none"
      >
        <div className="p-[16px]">{tableContent}</div>
      </AppCardTabListContainer>
      {modals}
    </AppCard>
  );
};

export default Document;
